import { useCallback, useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { Image } from 'expo-image';
import * as FileSystem from 'expo-file-system/legacy';

// Best-effort path of expo-image's underlying disk cache directory.
// These are SDWebImage / Glide internals, so they may shift in future expo-image
// versions. The hook degrades gracefully (returns null) if the path can't be read.
const CACHE_PATH: string | null = (() => {
  if (!FileSystem.cacheDirectory) return null;
  if (Platform.OS === 'ios') {
    return `${FileSystem.cacheDirectory}com.hackemist.SDImageCache/default/`;
  }
  if (Platform.OS === 'android') {
    return `${FileSystem.cacheDirectory}image_manager_disk_cache/`;
  }
  return null;
})();

const measureDirectorySize = async (uri: string): Promise<number> => {
  const info = await FileSystem.getInfoAsync(uri);
  if (!info.exists) return 0;
  if (!info.isDirectory) return info.size;

  const entries = await FileSystem.readDirectoryAsync(uri);
  let total = 0;
  for (const entry of entries) {
    const childUri = uri.endsWith('/') ? `${uri}${entry}` : `${uri}/${entry}`;
    total += await measureDirectorySize(childUri);
  }
  return total;
};

export interface IImageCacheStats {
  sizeBytes: number | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
  clearNow: () => Promise<void>;
}

export const useImageCacheStats = (): IImageCacheStats => {
  const [sizeBytes, setSizeBytes] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!CACHE_PATH) {
      setSizeBytes(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const total = await measureDirectorySize(CACHE_PATH);
      setSizeBytes(total);
    } catch {
      setSizeBytes(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearNow = useCallback(async () => {
    try {
      await Promise.all([Image.clearDiskCache(), Image.clearMemoryCache()]);
    } finally {
      await refresh();
    }
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { sizeBytes, isLoading, refresh, clearNow };
};
